import { useState } from "react";
import { ConfirmationModal } from "@packsize/components";
import type { User } from "@packsize/identity-api-sdk";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { IconButton, Stack, Typography } from "@mui/material";
import { useToast } from "@packsize/toast";
import { FormattedMessage, useIntl } from "react-intl";

import { useDeleteUser } from "@/hooks/user";

interface DeleteUserButtonProps {
	readonly user: User;
}

export const DeleteUserButton = ({ user }: DeleteUserButtonProps) => {
	const { mutateAsync: deleteUser, isPending } = useDeleteUser();
	const [open, setOpen] = useState(false);
	const intl = useIntl();
	const { toastSuccess, toastError } = useToast();

	const handleClose = () => {
		setOpen(false);
	};

	const onConfirm = async () => {
		try {
			await deleteUser(user);
			toastSuccess({
				message: intl.formatMessage(
					{
						id: `User {userName} deleted successfully`,
						defaultMessage: `User {userName} deleted successfully`,
					},
					{ userName: user.userName },
				),
			});
			handleClose();
		} catch {
			toastError({
				message: intl.formatMessage({ id: "Failed to delete user", defaultMessage: "Failed to delete user" }),
			});
		}
	};

	return (
		<>
			<IconButton
				onClick={() => {
					setOpen(true);
				}}
				data-cy="delete-button"
				data-pendoid="users__button__delete-users"
			>
				<DeleteOutlineIcon sx={{ fontSize: 24, color: "text.primary" }} />
			</IconButton>

			<ConfirmationModal
				open={open}
				onClose={handleClose}
				onCancel={handleClose}
				onConfirm={onConfirm}
				title={intl.formatMessage({ id: "Delete User", defaultMessage: "Delete User" })}
				confirmText={intl.formatMessage({ id: "Delete", defaultMessage: "Delete" })}
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				slotProps={{
					confirmButton: { loading: isPending },
				}}
			>
				<Stack spacing={1.5}>
					<Typography sx={{ textAlign: "justify" }}>
						<FormattedMessage id="This change can not be undone." defaultMessage="This change can not be undone." />
					</Typography>
				</Stack>
			</ConfirmationModal>
		</>
	);
};
